/**
 * 将 FSM aliasCountList 返回数据转换为 RoomStatus 组件所需的 RoomStatusData
 * 前端直调第三方后在此转换，不再依赖后端 /api/store-revenue/room-status
 *
 * 缓存拆分：
 * - 房型列表（id、name、hotelTypeSort）：不变，永久缓存，不再请求
 * - 每个房型的信息（数量、价格等）：会变，1 分钟有效，超时再查 FSM 更新
 */
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { COMPARISON_REFERENCE } from '@ms/shared-components';
var HOURLY_PRICE_NODES = 12;
function roundTwo(v) {
    return Math.round(v * 100) / 100;
}
/**
 * 时段内剔除重复价格后求平均；空时段返回 null
 */
export function averageOfUniquePricesInSlot(slot) {
    if (!slot.length)
        return null;
    var seen = new Set();
    var unique = [];
    for (var _i = 0, slot_1 = slot; _i < slot_1.length; _i++) {
        var price = slot_1[_i].price;
        var p = roundTwo(price);
        if (!seen.has(p)) {
            seen.add(p);
            unique.push(p);
        }
    }
    if (unique.length === 0)
        return null;
    var sum = unique.reduce(function (a, b) { return a + b; }, 0);
    return roundTwo(sum / unique.length);
}
/**
 * 从房型列表 + 动态信息拼出 RoomStatusData（用于双缓存合并）
 * @param avgPriceRefs 可选，去年今天/七天前平均价（来自 groupBusinessReportTotal），用于辅助线并标明日期
 * @param priceHistory 可选，每房型 12 时段的历史价格（带时间）；展示时按时段去重后取平均
 */
export function buildRoomStatusFromListAndData(roomTypeList, byId, avgPriceRefs, priceHistory) {
    if (!roomTypeList.length) {
        return { dataSource: [] };
    }
    var yearLabel = avgPriceRefs
        ? "\u5E74\u540C\u6BD4(".concat(avgPriceRefs.lastYearToday.date, ")")
        : '年同比';
    var weekLabel = avgPriceRefs
        ? "\u5468\u73AF\u6BD4(".concat(avgPriceRefs.sevenDaysAgo.date, ")")
        : '周环比';
    var yearValue = avgPriceRefs ? roundTwo(avgPriceRefs.lastYearToday.avgPrice) : undefined;
    var weekValue = avgPriceRefs ? roundTwo(avgPriceRefs.sevenDaysAgo.avgPrice) : undefined;
    var rows = roomTypeList.map(function (rt) {
        var d = byId[rt.id];
        var total = d ? d.totalRoomCount : 0;
        var open = d ? d.roomAvaliableCount : 0;
        var progress = total > 0 ? roundTwo(((total - open) / total) * 100) : 0;
        var label = (rt.name || "\u623F\u578B".concat(rt.id)).replace(/[（(][^）)]*[）)]/g, '').trim();
        var dayPrice = d ? d.dayPrice : 0;
        var history = priceHistory === null || priceHistory === void 0 ? void 0 : priceHistory.get(rt.id);
        var priceData = history && history.length === HOURLY_PRICE_NODES
            ? Array.from({ length: HOURLY_PRICE_NODES }, function (_, i) {
                var avg = averageOfUniquePricesInSlot(history[i]);
                return avg !== null ? avg : roundTwo(dayPrice);
            })
            : Array.from({ length: HOURLY_PRICE_NODES }, function () { return roundTwo(dayPrice); });
        var range = Math.max.apply(Math, __spreadArray(__spreadArray([], priceData, false), [0], false)) || 1;
        var normalizedReferenceLines = [
            {
                value: yearValue !== null && yearValue !== void 0 ? yearValue : roundTwo(range * 0.6),
                color: COMPARISON_REFERENCE.yearOverYear.color,
                strokeDasharray: COMPARISON_REFERENCE.yearOverYear.strokeDasharray,
                label: yearLabel,
            },
            {
                value: weekValue !== null && weekValue !== void 0 ? weekValue : roundTwo(range * 0.4),
                color: COMPARISON_REFERENCE.weekOverWeek.color,
                strokeDasharray: COMPARISON_REFERENCE.weekOverWeek.strokeDasharray,
                label: weekLabel,
            },
        ];
        return {
            id: "room-".concat(rt.id),
            label: label,
            progress: progress,
            quantity: total,
            quantityOpen: open,
            priceData: priceData,
            normalizedReferenceLines: normalizedReferenceLines,
        };
    });
    var totalQuantity = rows.reduce(function (s, r) { return s + r.quantity; }, 0);
    var totalOpen = rows.reduce(function (s, r) { var _a; return s + ((_a = r.quantityOpen) !== null && _a !== void 0 ? _a : 0); }, 0);
    var storeProgress = totalQuantity > 0 ? roundTwo(((totalQuantity - totalOpen) / totalQuantity) * 100) : 0;
    var len = HOURLY_PRICE_NODES;
    var storePriceData = rows.length > 0
        ? Array.from({ length: len }, function (_, i) {
            var sum = rows.reduce(function (s, r) { var _a; return s + ((_a = r.priceData[i]) !== null && _a !== void 0 ? _a : 0); }, 0);
            return roundTwo(sum / rows.length);
        })
        : Array.from({ length: len }, function () { return 0; });
    var storeTotalRow = {
        id: 'store-total',
        label: '全店',
        progress: storeProgress,
        quantity: totalQuantity,
        quantityOpen: totalOpen,
        priceData: storePriceData,
        normalizedReferenceLines: [
            {
                value: yearValue !== null && yearValue !== void 0 ? yearValue : 30,
                color: COMPARISON_REFERENCE.yearOverYear.color,
                strokeDasharray: COMPARISON_REFERENCE.yearOverYear.strokeDasharray,
                label: yearLabel,
            },
            {
                value: weekValue !== null && weekValue !== void 0 ? weekValue : 20,
                color: COMPARISON_REFERENCE.weekOverWeek.color,
                strokeDasharray: COMPARISON_REFERENCE.weekOverWeek.strokeDasharray,
                label: weekLabel,
            },
        ],
        isStoreTotal: true,
        progressColor: '#ff7a00',
        chartColor: '#722ed1',
    };
    return { dataSource: __spreadArray([storeTotalRow], rows, true) };
}
/**
 * 从 FSM 全量返回提取：房型列表（不变）+ 动态信息 byId（会变）
 */
export function splitFsmItems(items) {
    var roomTypeList = items
        .map(function (i) { var _a; return ({ id: i.id, name: i.name, hotelTypeSort: (_a = i.hotelTypeSort) !== null && _a !== void 0 ? _a : 0 }); })
        .sort(function (a, b) { return a.hotelTypeSort - b.hotelTypeSort; });
    var byId = {};
    items.forEach(function (i) {
        var _a, _b, _c;
        byId[i.id] = {
            totalRoomCount: (_a = i.totalRoomCount) !== null && _a !== void 0 ? _a : 0,
            roomAvaliableCount: (_b = i.roomAvaliableCount) !== null && _b !== void 0 ? _b : 0,
            dayPrice: (_c = i.dayPrice) !== null && _c !== void 0 ? _c : 0,
        };
    });
    return { roomTypeList: roomTypeList, byId: byId };
}
export function fsmToRoomStatusData(items) {
    if (!items.length) {
        return { dataSource: [] };
    }
    var rows = items.map(function (item) {
        var _a, _b, _c;
        var total = (_a = item.totalRoomCount) !== null && _a !== void 0 ? _a : 0;
        var open = (_b = item.roomAvaliableCount) !== null && _b !== void 0 ? _b : 0;
        var progress = total > 0 ? roundTwo(((total - open) / total) * 100) : 0;
        var label = (item.name || "\u623F\u578B".concat(item.id)).replace(/[（(][^）)]*[）)]/g, '').trim();
        var dayPrice = (_c = item.dayPrice) !== null && _c !== void 0 ? _c : 0;
        var priceData = Array.from({ length: HOURLY_PRICE_NODES }, function () { return roundTwo(dayPrice); });
        var range = Math.max.apply(Math, __spreadArray(__spreadArray([], priceData, false), [0], false)) || 1;
        var normalizedReferenceLines = [
            {
                value: roundTwo(range * 0.6),
                color: COMPARISON_REFERENCE.yearOverYear.color,
                strokeDasharray: COMPARISON_REFERENCE.yearOverYear.strokeDasharray,
                label: COMPARISON_REFERENCE.yearOverYear.label,
            },
            {
                value: roundTwo(range * 0.4),
                color: COMPARISON_REFERENCE.weekOverWeek.color,
                strokeDasharray: COMPARISON_REFERENCE.weekOverWeek.strokeDasharray,
                label: COMPARISON_REFERENCE.weekOverWeek.label,
            },
        ];
        return {
            id: "room-".concat(item.id),
            label: label,
            progress: progress,
            quantity: total,
            quantityOpen: open,
            priceData: priceData,
            normalizedReferenceLines: normalizedReferenceLines,
        };
    });
    var totalQuantity = rows.reduce(function (s, r) { return s + r.quantity; }, 0);
    var totalOpen = rows.reduce(function (s, r) { var _a; return s + ((_a = r.quantityOpen) !== null && _a !== void 0 ? _a : 0); }, 0);
    var storeProgress = totalQuantity > 0 ? roundTwo(((totalQuantity - totalOpen) / totalQuantity) * 100) : 0;
    var len = HOURLY_PRICE_NODES;
    var storePriceData = rows.length > 0
        ? Array.from({ length: len }, function (_, i) {
            var sum = rows.reduce(function (s, r) { var _a; return s + ((_a = r.priceData[i]) !== null && _a !== void 0 ? _a : 0); }, 0);
            return roundTwo(sum / rows.length);
        })
        : Array.from({ length: len }, function () { return 0; });
    var storeRefLines = [
        {
            value: 30,
            color: COMPARISON_REFERENCE.yearOverYear.color,
            strokeDasharray: COMPARISON_REFERENCE.yearOverYear.strokeDasharray,
            label: COMPARISON_REFERENCE.yearOverYear.label,
        },
        {
            value: 20,
            color: COMPARISON_REFERENCE.weekOverWeek.color,
            strokeDasharray: COMPARISON_REFERENCE.weekOverWeek.strokeDasharray,
            label: COMPARISON_REFERENCE.weekOverWeek.label,
        },
    ];
    var storeTotalRow = {
        id: 'store-total',
        label: '全店',
        progress: storeProgress,
        quantity: totalQuantity,
        quantityOpen: totalOpen,
        priceData: storePriceData,
        normalizedReferenceLines: storeRefLines,
        isStoreTotal: true,
        progressColor: '#ff7a00',
        chartColor: '#722ed1',
    };
    return {
        dataSource: __spreadArray([storeTotalRow], rows, true),
    };
}
